import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { getOrderHistory } from 'features/slices';
import moment from 'moment-timezone';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

const FIELD_LABELS = {
  status_fulfil: 'Status fulfil',
  is_message_customer: 'Message',
  is_deli: 'Deli',
  mai: 'Mai',
  nhung: 'Nhung',
  tracking: 'Tracking',
  fulfil: 'Fulfil',
  lineship: 'Lineship',
  cong: 'Cong',
  color: 'Color',
  size: 'Size',
  email: 'Email',
  note: 'Note',
  basecost: 'Basecost',
  price: 'Price',
  error_order: 'Error',
  shipping_method: 'Shipping',
  design_link: 'Design',
  fulfil_id: 'Fulfil id',
};

function formatValue(value) {
  if (value == null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Có' : 'Không';
  if (typeof value === 'object') {
    return Object.entries(value)
      .filter(([, item]) => item != null && item !== '' && item !== false)
      .map(([key, item]) => `${key}: ${typeof item === 'string' ? item : JSON.stringify(item)}`)
      .join(', ') || '—';
  }
  return String(value);
}

function changeLines(entry) {
  const after = entry.dataAfter && typeof entry.dataAfter === 'object' ? entry.dataAfter : null;
  const before = entry.dataBefore && typeof entry.dataBefore === 'object' ? entry.dataBefore : null;
  const source = after || before;
  if (!source) return [];
  return Object.keys(source).map((key) => {
    const label = FIELD_LABELS[key] || key;
    const next = after ? after[key] : undefined;
    const prev = before ? before[key] : undefined;
    if (before && after && Object.prototype.hasOwnProperty.call(before, key)) {
      return `${label}: ${formatValue(prev)} → ${formatValue(next)}`;
    }
    return `${label}: ${formatValue(next)}`;
  });
}

function extraNote(note) {
  if (!note) return '';
  if (/update order_id or id:/i.test(note)) return '';
  return note;
}

function actorName(user) {
  return String(user || 'Hệ thống').replace(/@gmail\.com$/i, '');
}

export default function OrderHistoryDialog({ open, item, user, onClose }) {
  const dispatch = useDispatch();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !item) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    setEntries([]);
    dispatch(getOrderHistory({
      id: item.id,
      order_id: item.order_id,
      store_name: item.store_name,
      user,
    })).then((res) => {
      if (cancelled) return;
      if (res.payload?.status === 0) {
        setEntries(Array.isArray(res.payload.data) ? res.payload.data : []);
      } else {
        setError(res.payload?.message || 'Không tải được lịch sử đơn');
      }
      setLoading(false);
    }).catch(() => {
      if (cancelled) return;
      setError('Không tải được lịch sử đơn');
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [open, item, user, dispatch]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>
        <span style={{ display: 'block', fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em' }}>Lịch sử đơn</span>
        <span style={{ display: 'block', marginTop: 4, fontSize: 13, fontWeight: 500, color: '#64748b' }}>
          {item?.order_id || '—'}
          {item?.store_name ? ` · ${item.store_name}` : ''}
        </span>
      </DialogTitle>
      <DialogContent dividers sx={{ background: '#f8fafc', minHeight: 180 }}>
        {loading ? (
          <p style={{ margin: 0, color: '#64748b', fontSize: 14 }}>Đang tải nhật ký...</p>
        ) : error ? (
          <p style={{ margin: 0, color: '#b91c1c', fontSize: 14 }}>{error}</p>
        ) : entries.length === 0 ? (
          <p style={{ margin: 0, color: '#64748b', fontSize: 14 }}>Chưa có nhật ký cho đơn này.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {entries.map((entry) => {
              const lines = changeLines(entry);
              const note = extraNote(entry.note);
              return (
                <article
                  key={entry.id}
                  style={{
                    background: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: 10,
                    padding: '10px 12px',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
                    <strong style={{ fontSize: 13, color: '#0f172a' }}>{entry.action || 'Update'}</strong>
                    <time style={{ fontSize: 12, color: '#64748b', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                      {entry.createdAt ? moment(entry.createdAt).format('HH:mm DD/MM/YYYY') : ''}
                    </time>
                  </div>
                  <div style={{ marginTop: 2, fontSize: 12, color: '#475569' }}>{actorName(entry.user)}</div>
                  {lines.map((line) => (
                    <div key={line} style={{ marginTop: 6, fontSize: 13, color: '#1e293b', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {line}
                    </div>
                  ))}
                  {note ? (
                    <div style={{ marginTop: 6, fontSize: 13, color: '#334155', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {note}
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Đóng</Button>
      </DialogActions>
    </Dialog>
  );
}

export function OrderHistoryButton({ onClick }) {
  return (
    <button type="button" className="tc-icon-btn" onClick={onClick} title="Lịch sử đơn">
      <HistoryOutlinedIcon />
    </button>
  );
}
